import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Truck, RefreshCw, Boxes, AlertTriangle, CheckCircle2, Download, Loader2, Sparkles, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function api() {
  const t = localStorage.getItem("token");
  return axios.create({
    baseURL: `${API}/api`,
    headers: { Authorization: `Bearer ${t}` },
  });
}

export default function AdminCJTab() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(""); // "tracking" | "stock" | "import" | ""
  // Import-by-SPU state
  const [spusInput, setSpusInput] = useState("");
  const [markup, setMarkup] = useState(3.0);
  const [translate, setTranslate] = useState(true);
  const [importResults, setImportResults] = useState(null);
  // Bestseller discovery
  const [discoverItems, setDiscoverItems] = useState(null);
  const [discoverSelection, setDiscoverSelection] = useState(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const r = await api().get("/admin/cj/status");
      setStatus(r.data);
    } catch (e) {
      toast.error("Impossible de charger l'état CJ");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const syncTracking = async () => {
    setBusy("tracking");
    try {
      const r = await api().post("/admin/cj/sync-tracking");
      toast.success(`Tracking sync : ${r.data.updated} mis à jour, ${r.data.errors} erreur(s)`);
      load();
    } catch (e) {
      toast.error("Sync tracking échoué");
    } finally {
      setBusy("");
    }
  };

  const syncStock = async () => {
    setBusy("stock");
    try {
      const r = await api().post("/admin/cj/sync-stock");
      toast.success(`Stock sync : ${r.data.updated} produit(s) mis à jour`);
      load();
    } catch (e) {
      toast.error("Sync stock échoué");
    } finally {
      setBusy("");
    }
  };

  const cleanupNonCjProducts = async () => {
    if (!window.confirm(
      "Voulez-vous vraiment supprimer TOUS les produits placeholder (sans cj_pid) ?\n\n" +
      "Les produits avec commandes payées sont conservés automatiquement.\n" +
      "Cette action est irréversible."
    )) return;
    setBusy("cleanup");
    try {
      const r = await api().post("/admin/cj/cleanup-non-cj-products");
      const d = r.data;
      toast.success(
        `${d.deleted} produit(s) supprimé(s) · ${d.reviews_deleted} avis nettoyés` +
        (d.skipped_due_to_paid_orders ? ` · ${d.skipped_due_to_paid_orders} ignoré(s) (commandes payées)` : "")
      );
    } catch (e) {
      toast.error("Cleanup échoué : " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy("");
    }
  };

  const importBySpu = async () => {
    const pids = spusInput
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (pids.length === 0) {
      toast.error("Collez au moins 1 SPU CJ");
      return;
    }
    if (pids.length > 20) {
      toast.error("Max 20 SPU par batch (rate-limit CJ)");
      return;
    }
    setBusy("import");
    setImportResults(null);
    try {
      const r = await api().post("/admin/cj/import-by-pid", {
        pids,
        markup: Number(markup) || 3.0,
        translate,
        auto_category: true,
      });
      setImportResults(r.data.results || []);
      const ok = (r.data.results || []).filter((x) => x.ok).length;
      const fail = (r.data.results || []).length - ok;
      if (fail === 0) {
        toast.success(`${ok} produit(s) importé(s) avec succès`);
      } else {
        toast.warning(`${ok} importé(s), ${fail} échec(s) — voir détails`);
      }
    } finally {
      setBusy("");
    }
  };

  const discoverBestsellers = async () => {
    setBusy("discover");
    setDiscoverItems(null);
    setDiscoverSelection(new Set());
    try {
      const r = await api().get("/admin/cj/discover-bestsellers?min_listed=15&max_per_category=6");
      setDiscoverItems(r.data.items || []);
      toast.success(`${(r.data.items || []).length} produits trending trouvés`);
    } catch (e) {
      toast.error("Discover échoué : " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy("");
    }
  };

  const toggleDiscoverItem = (spu) => {
    setDiscoverSelection((prev) => {
      const n = new Set(prev);
      if (n.has(spu)) n.delete(spu);
      else n.add(spu);
      return n;
    });
  };

  const importSelectedDiscover = async () => {
    const pids = [...discoverSelection];
    if (pids.length === 0) {
      toast.error("Sélectionnez au moins 1 produit");
      return;
    }
    setBusy("import-discover");
    try {
      const r = await api().post("/admin/cj/import-by-pid", {
        pids,
        markup: Number(markup) || 3.0,
        translate,
        auto_category: true,
      });
      const ok = (r.data.results || []).filter((x) => x.ok).length;
      toast.success(`${ok}/${pids.length} produit(s) importé(s)`);
      setDiscoverSelection(new Set());
      await discoverBestsellers();
    } catch (e) {
      toast.error("Import échoué : " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy("");
    }
  };

  if (loading || !status) {
    return (
      <div className="p-12 text-center text-sm text-white/60 border border-white/10 bg-[#0A2A4F]">
        Chargement…
      </div>
    );
  }

  return (
    <div className="space-y-10" data-testid="admin-cj-tab">
      {/* Header */}
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#C5CDD8] mb-3">
          Fulfillment
        </div>
        <h2 className="font-display text-3xl font-bold uppercase tracking-tighter text-white">
          Intégration CJ Dropshipping
        </h2>
        <p className="mt-4 text-sm text-white/65 max-w-2xl leading-relaxed">
          Les commandes payées sont automatiquement poussées vers CJ pour traitement.
          Les numéros de suivi sont récupérés via un sync manuel (ou cron à venir).
          Synchronisez aussi le stock pour vos produits liés à un{" "}
          <code className="text-[#FFD700]">cj_vid</code>.
        </p>
      </div>

      {/* Status banner */}
      <div
        className={`flex items-start gap-4 border px-6 py-5 ${
          status.configured
            ? "border-[#A6E0B5]/40 bg-[#A6E0B5]/10"
            : "border-[#FFD700]/40 bg-[#FFD700]/10"
        }`}
        data-testid="cj-status-banner"
      >
        {status.configured ? (
          <CheckCircle2 size={28} strokeWidth={1.5} className="text-[#A6E0B5] flex-shrink-0" />
        ) : (
          <AlertTriangle size={28} strokeWidth={1.5} className="text-[#FFD700] flex-shrink-0" />
        )}
        <div className="flex-1">
          <div className="text-sm font-bold text-white">
            {status.configured
              ? "Intégration active — connexion CJ établie"
              : "Intégration en attente — clés manquantes"}
          </div>
          <div className="text-xs text-white/70 mt-2 leading-relaxed">
            {status.configured ? (
              <>
                Base : <code className="text-[#FFD700]">{status.base_url}</code> · Logistique
                par défaut : <code className="text-[#FFD700]">{status.default_logistic}</code>
              </>
            ) : (
              <>
                Renseignez <code className="text-[#FFD700]">USER_ID_CJ</code> (email CJ) et{" "}
                <code className="text-[#FFD700]">API_KEY_CJ</code> dans <code>backend/.env</code>{" "}
                puis redémarrez le backend. Les commandes payées resteront en attente jusque-là.
              </>
            )}
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3 max-w-md text-[11px] uppercase tracking-widest font-bold">
            <div className={`flex items-center gap-2 ${status.email_set ? "text-[#A6E0B5]" : "text-white/40"}`}>
              ● USER_ID_CJ : {status.email_set ? "OK" : "vide"}
            </div>
            <div className={`flex items-center gap-2 ${status.api_key_set ? "text-[#A6E0B5]" : "text-white/40"}`}>
              ● API_KEY_CJ : {status.api_key_set ? "OK" : "vide"}
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <CjKpi
          icon={Truck}
          label="Commandes en attente"
          value={status.pending_orders}
          hint="Payées, pas encore poussées"
          testid="cj-kpi-pending"
        />
        <CjKpi
          icon={CheckCircle2}
          label="Commandes poussées"
          value={status.pushed_orders}
          hint="Envoyées à CJ avec succès"
          testid="cj-kpi-pushed"
        />
        <CjKpi
          icon={Boxes}
          label="Tracking récupéré"
          value={status.orders_with_tracking}
          hint="Numéro de suivi enregistré"
          testid="cj-kpi-tracking"
        />
      </div>

      {/* Actions */}
      <div className="border border-white/10 p-8 bg-[#0A2A4F]">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#C5CDD8] mb-5">
          Actions de synchronisation
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={syncTracking}
            disabled={!status.configured || busy === "tracking"}
            className="inline-flex items-center gap-2 bg-white text-[#001F3F] hover:bg-[#FFD700] transition-all uppercase tracking-[0.25em] text-xs font-bold px-6 py-3 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="cj-sync-tracking-btn"
          >
            <RefreshCw size={14} className={busy === "tracking" ? "animate-spin" : ""} />
            Synchroniser les numéros de suivi
          </button>
          <button
            onClick={syncStock}
            disabled={!status.configured || busy === "stock"}
            className="inline-flex items-center gap-2 border border-white/30 hover:bg-white/10 transition-all uppercase tracking-[0.25em] text-xs font-bold px-6 py-3 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="cj-sync-stock-btn"
          >
            <Boxes size={14} className={busy === "stock" ? "animate-spin" : ""} />
            Synchroniser les stocks
          </button>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 border border-white/30 hover:bg-white/10 transition-all uppercase tracking-[0.25em] text-xs font-bold px-6 py-3"
            data-testid="cj-refresh-btn"
          >
            <RefreshCw size={14} />
            Rafraîchir
          </button>
          <button
            onClick={cleanupNonCjProducts}
            disabled={busy === "cleanup"}
            className="inline-flex items-center gap-2 border border-red-400/40 text-red-300 hover:bg-red-400/10 transition-all uppercase tracking-[0.25em] text-xs font-bold px-6 py-3 disabled:opacity-40"
            data-testid="cj-cleanup-btn"
          >
            <AlertTriangle size={14} />
            {busy === "cleanup" ? "Nettoyage…" : "Supprimer les produits sans cj_pid"}
          </button>
        </div>
        <p className="mt-5 text-xs text-white/40 leading-relaxed">
          Astuce : assignez un <code className="text-[#FFD700]">cj_vid</code> (variant ID CJ) à
          chaque produit dans l'onglet <strong>Produits</strong> pour que la synchronisation
          puisse fonctionner.
        </p>
      </div>

      {/* Discover Bestsellers */}
      <div className="border border-[#FFD700]/30 p-8 bg-gradient-to-br from-[#0A2A4F] to-[#0A2A4F]/60" data-testid="cj-discover-section">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#FFD700] mb-2">
              Sourcing — Découverte IA
            </div>
            <h3 className="font-display text-2xl font-bold uppercase tracking-tighter text-white">
              Trouve les bestsellers CJ pour toi
            </h3>
            <p className="text-sm text-white/65 max-w-2xl leading-relaxed mt-3">
              Cliquez sur un bouton et nous interrogeons l'API CJ avec les catégories alignées
              sur Nordstella (Health, Wearable, Charger, Body Care…) pour vous afficher les
              produits trending (≥15 vendeurs CJ). Aucune navigation sur cjdropshipping.com requise.
            </p>
          </div>
          <button
            onClick={discoverBestsellers}
            disabled={busy === "discover" || !status?.configured}
            className="inline-flex items-center justify-center gap-2 bg-[#FFD700] text-[#001F3F] hover:bg-white transition-all uppercase tracking-[0.25em] text-xs font-bold px-6 py-3 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="cj-discover-btn"
          >
            {busy === "discover" ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {busy === "discover" ? "Recherche…" : "Lancer la découverte"}
          </button>
        </div>

        {discoverItems && discoverItems.length === 0 && (
          <div className="text-sm text-white/60 italic mt-6">
            Aucun produit trending trouvé pour les catégories niche. Essayez l'import par SPU ci-dessous.
          </div>
        )}

        {discoverItems && discoverItems.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="text-xs font-bold uppercase tracking-widest text-white/70">
                {discoverItems.length} produits trouvés · {discoverSelection.size} sélectionné(s)
              </div>
              <button
                onClick={importSelectedDiscover}
                disabled={discoverSelection.size === 0 || busy === "import-discover"}
                className="inline-flex items-center justify-center gap-2 bg-white text-[#001F3F] hover:bg-[#FFD700] transition-all uppercase tracking-[0.25em] text-xs font-bold px-5 py-2.5 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="cj-discover-import-selected-btn"
              >
                {busy === "import-discover" ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                Importer la sélection
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {discoverItems.map((it) => {
                const sel = discoverSelection.has(it.spu);
                const dis = it.already_imported;
                return (
                  <button
                    key={it.spu}
                    type="button"
                    onClick={() => !dis && toggleDiscoverItem(it.spu)}
                    disabled={dis}
                    data-testid={`cj-discover-card-${it.spu}`}
                    className={`relative text-left border p-4 transition-all ${
                      dis
                        ? "border-white/10 bg-white/5 opacity-50 cursor-not-allowed"
                        : sel
                        ? "border-[#FFD700] bg-[#FFD700]/10"
                        : "border-white/15 bg-[#001F3F]/40 hover:border-white/40"
                    }`}
                  >
                    {dis && (
                      <span className="absolute top-2 right-2 text-[9px] uppercase tracking-widest font-bold text-[#A6E0B5] bg-[#A6E0B5]/15 px-2 py-1 flex items-center gap-1">
                        <Check size={10} /> Importé
                      </span>
                    )}
                    {sel && !dis && (
                      <span className="absolute top-2 right-2 text-[9px] uppercase tracking-widest font-bold text-[#001F3F] bg-[#FFD700] px-2 py-1 flex items-center gap-1">
                        <Check size={10} /> OK
                      </span>
                    )}
                    <div className="flex gap-3">
                      <div className="w-16 h-16 bg-white/5 flex-shrink-0 overflow-hidden">
                        {it.image && (
                          <img src={it.image} alt="" className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-mono text-white/40 truncate">{it.spu}</div>
                        <div className="text-sm font-bold text-white line-clamp-2 mt-1 leading-snug">
                          {it.name_en}
                        </div>
                        <div className="flex items-center gap-3 mt-2 text-[11px]">
                          <span className="text-[#FFD700] font-bold">${it.price_usd}</span>
                          <span className="text-white/50">listed {it.listed}</span>
                        </div>
                        <div className="text-[10px] uppercase tracking-widest text-white/40 mt-1">
                          {it.category}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Import by SPU */}
      <div className="border border-white/10 p-8 bg-[#0A2A4F]" data-testid="cj-import-section">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#C5CDD8] mb-2">
          Sourcing — Import express par SPU
        </div>
        <h3 className="font-display text-2xl font-bold uppercase tracking-tighter text-white mb-3">
          Importer des produits CJ
        </h3>
        <p className="text-sm text-white/65 max-w-2xl leading-relaxed mb-6">
          Collez 1 ou plusieurs SPU CJ (séparés par retour à la ligne, espace ou virgule).
          Exemple : <code className="text-[#FFD700]">CJAM2754096</code>. Le système récupère
          automatiquement le nom, les images, les variantes et le <code className="text-[#FFD700]">cj_vid</code>,
          traduit en français (si activé) puis publie sur la boutique.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <label className="block text-[11px] font-bold uppercase tracking-[0.25em] text-white/60 mb-2">
              SPU CJ (1 par ligne, max 20)
            </label>
            <textarea
              value={spusInput}
              onChange={(e) => setSpusInput(e.target.value)}
              placeholder={"CJAM2754096\nCJSJ2841623\nCJJT2388857"}
              rows={8}
              className="w-full bg-[#001F3F] border border-white/15 text-white font-mono text-sm px-4 py-3 focus:outline-none focus:border-[#FFD700] placeholder:text-white/25"
              data-testid="cj-import-spus-textarea"
            />
          </div>
          <div className="space-y-5">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-[0.25em] text-white/60 mb-2">
                Marge (×)
              </label>
              <input
                type="number"
                step="0.1"
                min="1.5"
                max="6"
                value={markup}
                onChange={(e) => setMarkup(e.target.value)}
                className="w-full bg-[#001F3F] border border-white/15 text-white text-sm px-4 py-3 focus:outline-none focus:border-[#FFD700]"
                data-testid="cj-import-markup-input"
              />
              <div className="text-[11px] text-white/40 mt-1">
                Prix vente = coût USD × 0.93 × marge (arrondi à .X9)
              </div>
            </div>
            <label className="flex items-center gap-3 cursor-pointer text-sm text-white/80">
              <input
                type="checkbox"
                checked={translate}
                onChange={(e) => setTranslate(e.target.checked)}
                className="accent-[#FFD700] w-4 h-4"
                data-testid="cj-import-translate-checkbox"
              />
              Traduire en français (IA)
            </label>
            <button
              onClick={importBySpu}
              disabled={busy === "import" || !status?.configured}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#FFD700] text-[#001F3F] hover:bg-white transition-all uppercase tracking-[0.25em] text-xs font-bold px-6 py-3 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="cj-import-submit-btn"
            >
              {busy === "import" ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Download size={14} />
              )}
              {busy === "import" ? "Import en cours…" : "Importer"}
            </button>
            <div className="text-[11px] text-white/40 leading-relaxed">
              CJ limite à 1 requête/sec donc 5 produits ≈ 30s, 10 produits ≈ 60s.
            </div>
          </div>
        </div>

        {/* Results */}
        {importResults && (
          <div className="mt-8 border-t border-white/10 pt-6" data-testid="cj-import-results">
            <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/60 mb-4">
              Résultat de l'import ({importResults.length})
            </div>
            <div className="space-y-2">
              {importResults.map((r, i) => (
                <div
                  key={i}
                  className={`flex items-start gap-3 text-sm border px-4 py-3 ${
                    r.ok
                      ? "border-[#A6E0B5]/30 bg-[#A6E0B5]/5"
                      : "border-red-400/30 bg-red-400/5"
                  }`}
                >
                  {r.ok ? (
                    <CheckCircle2 size={18} className="text-[#A6E0B5] flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertTriangle size={18} className="text-red-400 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-xs text-white/70">{r.pid}</div>
                    {r.ok ? (
                      <div className="text-white text-sm mt-1">
                        <span className="font-bold">{r.name}</span>
                        <span className="text-white/60 ml-2">
                          · {r.price_eur}€ · {r.variants} variante(s) · {r.action}
                        </span>
                      </div>
                    ) : (
                      <div className="text-red-300 text-xs mt-1">{r.error}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CjKpi({ icon: Icon, label, value, hint, testid }) {
  return (
    <div className="border border-white/10 bg-white/5 p-6" data-testid={testid}>
      <Icon size={22} strokeWidth={1.5} className="text-[#C5CDD8]" />
      <div className="text-xs font-bold uppercase tracking-[0.25em] text-white/60 mt-4">{label}</div>
      <div className="font-display text-4xl font-bold tracking-tight text-white mt-2">{value}</div>
      <div className="text-xs text-white/40 mt-1">{hint}</div>
    </div>
  );
}
